import React, { useRef, useState } from "react";
import { Grid } from "@mui/material";
import { FormButton, SpinnerOverlay } from "components";
import {
  FormikAutocompleteInput,
  FormikCheckboxInput,
  FormikTextInput,
} from "components/UI/Inputs/FormikInputs";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { LARGO, mensajeLargo } from "consts/limites-campos";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { snackbarData, userCredentials } from "recoilState/GlobalState";
import styles from "styles/pages/ModalActions.module.scss";
import {
  CODIGOS_CON_TODOS,
  COMPONENTES_CON_TODOS,
} from "consts/tipos-de-componente";
import { RNHCargoIns } from "services/services-mia/programa-salud/personal/RNHCargoIns";

/**
 * Alta de un cargo: nombre, código y el componente de la estructura al que aplica.
 */
export default function InsertModal({ updateList, closeModal }) {
  const setSnackBarInfo = useSetRecoilState(snackbarData);
  const credentials = useRecoilValue(userCredentials);
  const [inRequest, setInRequest] = useState(false);
  const formikRef = useRef();

  const crearCargo = async (values) => {
    setInRequest(true);
    const response = await RNHCargoIns(credentials, {
      name: values.nombre,
      cod: values.cod,
      componenteCod: values.componenteCod,
      puedeEditar: values.puedeEditar,
    });
    setInRequest(false);
    // El servicio devuelve [] cuando salió bien; si falló, el interceptor de
    // axiosPost ya mostró el snackbar con el mensaje del back.
    if (response.ok) {
      setSnackBarInfo({
        message: "Cargo creado correctamente",
        severity: "success",
        open: true,
      });
      formikRef.current.resetForm();
      updateList();
      closeModal();
    }
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        nombre: "",
        cod: "",
        componenteCod: "",
        puedeEditar: "N",
      }}
      validationSchema={yup.object({
        nombre: yup
          .string()
          .max(LARGO.nombre, mensajeLargo(LARGO.nombre))
          .required("Debe ingresar un nombre"),
        cod: yup
          .string()
          .max(LARGO.cod, mensajeLargo(LARGO.cod))
          .required("Debe ingresar un código"),
        // Sin `required`: el vacio es la opcion "Todos", no un campo sin completar.
        componenteCod: yup.string().oneOf(CODIGOS_CON_TODOS)
      })}
      onSubmit={(values) => {
        crearCargo(values);
      }}
    >
      <Form className={styles.inputs} noValidate>
        <div
          className={styles["column"]}
        >
          <Grid
            container
            rowSpacing={0}
            columnSpacing={2}
            sx={{
              width: "100%",
              "& .MuiFormControl-root": { marginTop: 0, marginBottom: 0 },
            }}
          >
            {/* Los tres en un renglon: el codigo es corto, el nombre se lleva el
                ancho que sobra y el componente entra en un campo con buscador. */}
            <Grid item xs={3}>
              <FormikTextInput
                fullWidth={true}
                name="cod"
                labelText="Código"
                inputProps={{ maxLength: LARGO.cod }}
              />
            </Grid>
            <Grid item xs={5}>
              <FormikTextInput
                fullWidth={true}
                name="nombre"
                labelText="Nombre"
                inputProps={{ maxLength: LARGO.nombre }}
              />
            </Grid>
            <Grid item xs={4}>
              <FormikAutocompleteInput
                name="componenteCod"
                labelText="Repartición"
                options={COMPONENTES_CON_TODOS}
                optionValue="cod"
                optionLabel="label"
                // El vacio no es "falta elegir" sino la opcion "Todos", que viaja
                // como null. Por eso no hay placeholder: siempre hay algo elegido.
              />
            </Grid>
            {/* En su propio renglón y no al lado de los otros tres: no es un dato del
                cargo como el nombre o la repartición, es qué habilita tenerlo. */}
            <Grid item xs={12}>
              <FormikCheckboxInput
                name="puedeEditar"
                labelText="Puede editar"
              />
            </Grid>
          </Grid>
          <FormButton
            inRequest={inRequest}
            newFormData={{ button: "Crear" }}
            onCloseModal={closeModal}
          />
          {(inRequest) && <SpinnerOverlay />}
        </div>
      </Form>
    </Formik>
  );
}
